import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const AuthContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('session_token') || null);

  const checkAuth = useCallback(async () => {
    // CRITICAL: skip /me check if we're processing an OAuth callback
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API}/api/auth/me`, {
        method: 'GET',
        credentials: 'include',
        headers,
      });
      if (!res.ok) throw new Error('not authed');
      const data = await res.json();
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const persistToken = (t) => {
    if (t) {
      localStorage.setItem('session_token', t);
    } else {
      localStorage.removeItem('session_token');
    }
    setToken(t);
  };

  const login = async (email, password) => {
    const res = await fetch(`${API}/api/auth/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Login failed');
    }
    const data = await res.json();
    persistToken(data.session_token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const res = await fetch(`${API}/api/auth/register`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const detail = err.detail;
      if (detail && typeof detail === 'object') {
        // Structured error (e.g. Iter395 duplicate_account) — keep the payload
        // so the page can localise + render a sign-in link.
        const e = new Error(detail.message_en || detail.code || 'Registration failed');
        e.detail = detail;
        throw e;
      }
      throw new Error(detail || 'Registration failed');
    }
    const data = await res.json();
    persistToken(data.session_token);
    setUser(data.user);
    // Return the full response so callers can pull session_token directly
    // without racing the localStorage write (matters for follow-up uploads
    // like the photo + entitlement cards on the registration page).
    return data;
  };

  const exchangeGoogle = async (sessionId) => {
    const res = await fetch(`${API}/api/auth/google/session`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Google session exchange failed');
    }
    const data = await res.json();
    persistToken(data.session_token);
    setUser(data.user);
    return data.user;
  };

  // Iter223 — Custom Google OAuth: SPA gets back ?code=... on
  // /auth/callback, POSTs it here, backend exchanges with our own
  // client_secret and mints a session. Replaces the Emergent-managed
  // exchangeGoogle for new sign-ins.
  //
  // Iter234 — First-time Google sign-ups no longer get a user document
  // on the spot. The backend replies with `{needs_completion: true,
  // signup_token, google_profile}`. We surface that intact so the caller
  // (AuthCallback) can send the user through the "/complete-profile"
  // wall without ever polluting the members list.
  const exchangeGoogleOAuthCode = async (code, redirectUri) => {
    const res = await fetch(`${API}/api/auth/google/oauth-exchange`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, redirect_uri: redirectUri }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Google OAuth exchange failed');
    }
    const data = await res.json();
    if (data && data.needs_completion) {
      // No session issued yet — caller must route to /complete-profile.
      return { needsCompletion: true, signupToken: data.signup_token, googleProfile: data.google_profile || {} };
    }
    persistToken(data.session_token);
    setUser(data.user);
    return data.user;
  };

  // Finalize a pending Google sign-up: POSTs the mandatory-fields form
  // + photo to /api/auth/google/complete-signup, which only NOW creates
  // the actual user record and issues a real session.
  const completeGoogleSignup = async ({ signupToken, profile, consents, photoFile }) => {
    const fd = new FormData();
    fd.append('signup_token', signupToken);
    fd.append('profile_json', JSON.stringify(profile));
    fd.append('consents_json', JSON.stringify(consents || {}));
    if (photoFile) fd.append('photo', photoFile);
    const res = await fetch(`${API}/api/auth/google/complete-signup`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Signup finalisation failed');
    }
    const data = await res.json();
    persistToken(data.session_token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      await fetch(`${API}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers,
      });
    } catch {
      /* swallow */
    }
    persistToken(null);
    setUser(null);
  };

  const updateProfile = async (profile) => {
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${API}/api/auth/me`, {
      method: 'PUT',
      credentials: 'include',
      headers,
      body: JSON.stringify({ profile }),
    });
    if (!res.ok) throw new Error('Update failed');
    const data = await res.json();
    setUser(data);
    return data;
  };

  const authFetch = useCallback(
    async (path, options = {}) => {
      // Don't force JSON content-type for FormData (uploads need multipart boundary)
      const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
      const headers = isFormData
        ? { ...(options.headers || {}) }
        : { 'Content-Type': 'application/json', ...(options.headers || {}) };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      return fetch(`${API}${path}`, { credentials: 'include', ...options, headers });
    },
    [token]
  );

  return (
    <AuthContext.Provider
      value={{ user, loading, token, login, register, exchangeGoogle, exchangeGoogleOAuthCode, completeGoogleSignup, logout, updateProfile, authFetch, refresh: checkAuth }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
